import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  Modal,
  Drawer,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatDateTime,
  type BadgeColor,
} from '../components/ui';

interface Project {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'archived';
}

interface Task {
  id: string;
  title: string;
  description?: string;
  projectId?: string | null;
  status: 'backlog' | 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string | null;
  dueAt?: string | null;
  createdAt: string;
}

interface Label {
  id: string;
  name: string;
  color: string;
}

interface Comment {
  id: string;
  body: string;
  userId: string;
  createdAt: string;
}

const columns: { value: Task['status']; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

const priorityColor: Record<Task['priority'], BadgeColor> = {
  low: 'gray',
  medium: 'blue',
  high: 'yellow',
  urgent: 'red',
};

export function TasksPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Task | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState<string>('');
  const qc = useQueryClient();

  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ data: Project[] }>('/projects').catch(() => ({ data: [] })),
  });

  const tasksQ = useQuery({
    queryKey: ['tasks', projectFilter],
    queryFn: () => {
      const params: Record<string, string> = { limit: '100' };
      if (projectFilter) params.projectId = projectFilter;
      return api.get<{ data: Task[] }>('/tasks', params).catch(() => ({ data: [] }));
    },
  });

  const moveMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Task['status'] }) =>
      api.patch(`/tasks/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const projects = projectsQ.data?.data ?? [];
  const allTasks = tasksQ.data?.data ?? [];

  function handleDrop(status: Task['status']) {
    if (draggedId) {
      moveMut.mutate({ id: draggedId, status });
      setDraggedId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Tasks"
        subtitle={`${allTasks.length} tasks`}
        action={<Button onClick={() => setShowCreate(true)}>+ New task</Button>}
      />

      <div style={{ marginBottom: 'var(--space-4)', display: 'flex', gap: 'var(--space-2)' }}>
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {tasksQ.isLoading ? (
        <Spinner />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))`, gap: 'var(--space-3)', overflowX: 'auto' }}>
          {columns.map((col) => {
            const colTasks = allTasks.filter((t) => t.status === col.value);
            return (
              <div
                key={col.value}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(col.value)}
                style={{
                  background: 'var(--color-surface-secondary)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-3)',
                  minHeight: 400,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                  <strong style={{ fontSize: 'var(--text-sm)' }}>{col.label}</strong>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{colTasks.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {colTasks.map((t) => (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={() => setDraggedId(t.id)}
                      onClick={() => setSelected(t)}
                      style={{
                        background: 'var(--color-surface-primary)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-3)',
                        border: '1px solid var(--color-border-primary)',
                        cursor: 'grab',
                        boxShadow: 'var(--shadow-sm)',
                      }}
                    >
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>{t.title}</div>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Badge color={priorityColor[t.priority]}>{t.priority}</Badge>
                        {t.dueAt && (
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                            {new Date(t.dueAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {colTasks.length === 0 && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textAlign: 'center', padding: 'var(--space-3)' }}>
                      Drop tasks here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <TaskModal
          projects={projects}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            setShowCreate(false);
          }}
        />
      )}

      {selected && (
        <TaskDrawer
          task={selected}
          projects={projects}
          onClose={() => setSelected(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function TaskModal({ projects, onClose, onSaved }: { projects: Project[]; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState<Task['priority']>('medium');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/tasks', {
        title,
        description: description || undefined,
        projectId: projectId || undefined,
        status: 'todo',
        priority,
      }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New task">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!title) return setError('Title required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Title *">
          <Input value={title} onChange={setTitle} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Project">
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={selectStyle}>
              <option value="">None</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <Select
              value={priority}
              onChange={setPriority}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
            />
          </Field>
        </div>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TaskDrawer({
  task,
  projects,
  onClose,
  onSaved,
}: {
  task: Task;
  projects: Project[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority);
  const [projectId, setProjectId] = useState(task.projectId ?? '');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const labelsQ = useQuery({
    queryKey: ['task-labels'],
    queryFn: () => api.get<{ data: Label[] }>('/task-labels').catch(() => ({ data: [] })),
  });

  const commentsQ = useQuery({
    queryKey: ['task-comments', task.id],
    queryFn: () => api.get<{ data: Comment[] }>(`/tasks/${task.id}/comments`).catch(() => ({ data: [] })),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      api.patch(`/tasks/${task.id}`, {
        title,
        description: description || undefined,
        status,
        priority,
        projectId: projectId || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onSaved();
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/tasks/${task.id}`),
    onSuccess: onSaved,
  });

  const commentMut = useMutation({
    mutationFn: () => api.post(`/tasks/${task.id}/comments`, { body: comment }),
    onSuccess: () => {
      setComment('');
      qc.invalidateQueries({ queryKey: ['task-comments', task.id] });
    },
  });

  return (
    <Drawer onClose={onClose} title="Task">
      <Field label="Title">
        <Input value={title} onChange={setTitle} />
      </Field>
      <Field label="Description">
        <Textarea value={description} onChange={setDescription} rows={4} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
        <Field label="Status">
          <Select value={status} onChange={setStatus} options={columns.map((c) => ({ value: c.value, label: c.label }))} />
        </Field>
        <Field label="Priority">
          <Select
            value={priority}
            onChange={setPriority}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'urgent', label: 'Urgent' },
            ]}
          />
        </Field>
      </div>
      <Field label="Project">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={selectStyle}>
          <option value="">None</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>

      {labelsQ.data?.data && labelsQ.data.data.length > 0 && (
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-1)' }}>
            Labels
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
            {labelsQ.data.data.map((l) => (
              <span
                key={l.id}
                style={{
                  background: l.color,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 500,
                }}
              >
                {l.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, margin: '0 0 var(--space-2)' }}>Comments</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          {(commentsQ.data?.data ?? []).map((c) => (
            <Card key={c.id} style={{ padding: 'var(--space-2) var(--space-3)' }}>
              <div style={{ fontSize: 'var(--text-sm)' }}>{c.body}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {formatDateTime(c.createdAt)}
              </div>
            </Card>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Input value={comment} onChange={setComment} placeholder="Add a comment…" style={{ flex: 1 }} />
          <Button onClick={() => comment && commentMut.mutate()} disabled={commentMut.isPending || !comment}>
            Post
          </Button>
        </div>
      </div>

      <ErrorBanner error={error} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto' }}>
        <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
          Delete
        </Button>
        <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Drawer>
  );
}

const selectStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface-primary)',
};
